// Pure reading of what `apiPostFormData` returns for the import connector (story 194, contracts C3 and C4).
// It resolves with a 2xx body (or undefined after a redirect to /connection_error) or throws { status, name, data, message }.

export const VIEW = {
  IMPORTED: 'imported',
  REJECTED: 'rejected',
  UNDONE_FAILURE: 'undoneFailure',
  CONNECTOR_UNAVAILABLE: 'connectorUnavailable',
  NOT_CONFIGURED: 'notConfigured',
  FORBIDDEN: 'forbidden',
  UNKNOWN: 'unknown'
}

// The 8 wire literals of C4; a thrown `imported` is deliberately absent from the thrown mapping (G11).
const VIEW_BY_THROWN_STATUS = {
  invalid: VIEW.REJECTED,
  bad_request: VIEW.REJECTED,
  mic_error: VIEW.UNDONE_FAILURE,
  mic_unavailable: VIEW.UNDONE_FAILURE,
  connector_unavailable: VIEW.CONNECTOR_UNAVAILABLE,
  unknown_outcome: VIEW.UNKNOWN,
  not_configured: VIEW.NOT_CONFIGURED
}
const IMPORTED_LITERAL = 'imported'
const FORBIDDEN_HTTP_STATUS = 403

const isObject = (value) => typeof value === 'object' && value !== null
const asText = (value) => typeof value === 'string' ? value : ''
const asCount = (value) => Number.isInteger(value) && value > 0 ? value : 0
const asList = (value) => Array.isArray(value) ? value : []

/** Body fields of C3 with safe defaults ('' / 0 / []). */
const bodyFields = (body) => {
  const data = isObject(body) ? body : {}
  return {
    message: asText(data.message),
    mainPartReference: asText(data.mainPartReference),
    partReferences: asCount(data.partReferences),
    ignoredRows: asCount(data.ignoredRows),
    errors: asList(data.errors),
    fabricationOrders: asList(data.fabricationOrders),
    leftovers: asList(data.leftovers)
  }
}

const result = (view, body) => ({ view, ...bodyFields(body) })

const readThrown = (error) => {
  // Any 403 is "no permission", with or without a body: the one of `@PreAuthorize` is written by Spring Security,
  // empty or with its default error JSON (numeric status), never with a status of C4.
  if (error.status === FORBIDDEN_HTTP_STATUS) return result(VIEW.FORBIDDEN, {})
  const data = error.data
  if (!isObject(data)) return result(VIEW.UNKNOWN, {})
  // Own-property check: a status such as "constructor" must not resolve to something inherited.
  const known = typeof data.status === 'string' && Object.prototype.hasOwnProperty.call(VIEW_BY_THROWN_STATUS, data.status)
  return result(known ? VIEW_BY_THROWN_STATUS[data.status] : VIEW.UNKNOWN, data)
}

/**
 * Classifies the outcome of the import call.
 * @param {{ resolved?: any, thrown?: any }} outcome exactly one side is meaningful: `thrown` set when the call threw
 * @returns {{view: string, message: string, mainPartReference: string, partReferences: number, ignoredRows: number,
 *   errors: Array, fabricationOrders: Array, leftovers: Array}}
 */
export const readImportResult = ({ resolved, thrown } = {}) => {
  if (isObject(thrown)) return readThrown(thrown)
  if (isObject(resolved) && resolved.status === IMPORTED_LITERAL) return result(VIEW.IMPORTED, resolved)
  return result(VIEW.UNKNOWN, {})
}

const ORDER_PREFIX = 'order.'
const LABEL_KEY_BY_COLUMN = {
  'order.code': 'Fabrication Order',
  'order.targetDate': 'Target date',
  'order.priority': 'Priority',
  'order.clientCode': 'Client code',
  'order.planificationType': 'Plan'
}
export const WHOLE_FILE_KEY = 'Whole file'

/**
 * Rows of the errors table (S49, S50). Returns keys and texts, never translated strings:
 * `lineKey`/`columnKey` are i18n keys to translate; `line`/`column` are shown as they are.
 */
export const presentConnectorErrors = (errors) => asList(errors).map((error) => {
  const column = isObject(error) ? error.column : ''
  const columnText = typeof column === 'string' ? column : ''
  const isOrderColumn = columnText.startsWith(ORDER_PREFIX)
  const hasLine = isObject(error) && Number.isInteger(error.line)
  const labelKey = Object.prototype.hasOwnProperty.call(LABEL_KEY_BY_COLUMN, columnText) ? LABEL_KEY_BY_COLUMN[columnText] : ''
  return {
    line: hasLine ? error.line : '',
    lineKey: !hasLine && !isOrderColumn ? WHOLE_FILE_KEY : '',
    column: labelKey ? '' : columnText,
    columnKey: labelKey,
    reason: isObject(error) ? asText(error.message) : ''
  }
})
